import { ChannelType, CreateSubscriptionRequest, Recipient, RecipientType, Subscription } from '@eagle/core-data-types';
import { useSnackbar } from 'notistack';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthenticated } from '../../auth';
import { getBrowserLocale, getCanonicalTimeZone, useBoolFlag } from '../../components';
import { T_ONE } from '../../constants';
import { useApiErrorHandler, useCustomRoutes } from '../../hooks';
import { CacheDataTypes } from '../../types';
import { isIndividualUserRecipient } from '../subscription/subscription-detail.utils';
import { CreateSubscriptionView } from './create-subscription-view';
import { CreateSubscriptionFields } from './create-subscription.types';

interface CreateSubscriptionControllerProps {
  isProfilePage: boolean;
}

export const CreateSubscriptionController: FC<CreateSubscriptionControllerProps> = ({ isProfilePage }) => {
  const { subscription: subscriptionRoute } = useCustomRoutes();
  const { restClient, userInfo } = useAuthenticated();
  const { handleCreateError } = useApiErrorHandler();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation(['admin', 'common']);
  const [activeStep, setActiveStep] = useState<number>(0);
  const [createInProgress, setCreateInProgress] = useState(false);
  const isSubscriptionsV2Enabled = useBoolFlag('admin-subscriptions-v2-feature-temporary');

  const onActiveNext = (): void => setActiveStep((prevStep) => prevStep + 1);
  const onActiveBack = (): void => setActiveStep((prevStep) => prevStep - 1);

  const defaultRecipient: Recipient = {
    type: RecipientType.INTERNAL_INDIVIDUAL_USER,
    localization: {
      locale: getBrowserLocale(),
      timezone: getCanonicalTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone),
    },
    referenceId: '',
    channel: {
      type: ChannelType.EMAIL,
      address: null,
    },
  };

  const [subscription, setSubscription] = useState<CreateSubscriptionFields>({
    accountId: userInfo.accountId,
    recipient: defaultRecipient,
    alertFilters: {
      alertTypeId: null,
      feature: null,
      featureInstance: null,
      featureTypeIdPrefix: null,
      groupId: null,
      personId: null,
      thingId: null,
    },
  });

  const updateAlertFilters = useCallback((values: Partial<CreateSubscriptionFields['alertFilters']>) => {
    setSubscription((subscription) => ({
      ...subscription,
      alertFilters: {
        ...subscription.alertFilters,
        ...values,
      },
    }));
  }, []);

  const updateRecipient = (recipient: Recipient): void => {
    setSubscription({
      ...subscription,
      recipient,
    });
  };

  const getUpdatableProps = (): CreateSubscriptionRequest => {
    const feature =
      subscription.alertFilters.feature ?
        `${subscription.alertFilters.feature}${subscription.alertFilters.featureInstance ? `/${subscription.alertFilters.featureInstance}` : ''}`
        : null;

    const result: CreateSubscriptionRequest = {
      ...subscription,
      alertFilters: {
        alertTypeId: subscription.alertFilters.alertTypeId,
        feature,
        featureTypeIdPrefix: subscription.alertFilters.featureTypeIdPrefix,
        groupId: subscription.alertFilters.groupId,
        personId: subscription.alertFilters.personId,
        thingId: subscription.alertFilters.thingId,
      },
    };

    return result;
  };

  const createSubscription = async (): Promise<void> => {
    const create = (): Promise<Subscription> => {
      const body = getUpdatableProps();

      if (isProfilePage) {
        if (!isIndividualUserRecipient(body.recipient)) {
          throw new Error('Invalid state.');
        }
        return restClient.my.subscription.create({
          ...body,
          recipient: {
            channel: body.recipient.channel,
            localization: body.recipient.localization,
          },
        });
      }
      return restClient.subscription.create(body);
    };

    try {
      setCreateInProgress(true);
      const createdSubscription = await create();
      enqueueSnackbar(t('common:common.hint.create-success', { display: t('common:terms.subscription', { count: T_ONE }) }), { variant: 'success' });
      navigate(`${isProfilePage ? `/profile/${subscriptionRoute}` : `/recipient/${subscriptionRoute}`}/${createdSubscription._id}`);
    } catch (err) {
      handleCreateError(err, CacheDataTypes.SUBSCRIPTION, t('common:terms.subscription', { count: T_ONE }));
    } finally {
      setCreateInProgress(false);
    }
  };

  return (
    <CreateSubscriptionView
      activeStep={activeStep}
      createInProgress={createInProgress}
      createSubscription={createSubscription}
      isProfilePage={isProfilePage}
      isSubscriptionsV2Enabled={Boolean(isSubscriptionsV2Enabled)}
      onActiveBack={onActiveBack}
      onActiveNext={onActiveNext}
      subscription={subscription}
      updateAlertFilters={updateAlertFilters}
      updateRecipient={updateRecipient}
    />
  );
};
